import React, { useState, useEffect } from 'react';
import { IState } from '../../redux/redux';
import { IFmsApp, IAppTheme, IAppConfig } from './MobileAppTypes';
import { toast } from 'react-toastify';
import Button from '../_Core/Button';
import ColorPicker from '../Tools/ColorPicker';
import { EasyFileInput } from '../Tools/ExpandableDiv';
import MobileAppScreenManager from './MobileAppScreenManager';
import Icon, { IconType } from '../Icon/Icon';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

interface IProps {
  state: IState;
  app?: IFmsApp;
  onSave: (app: IFmsApp, fileDic: Record<string, File>) => void;
  onClose: () => void;
  onRemove: (app: IFmsApp) => void;
}

export const MobileAppEditor: React.FC<IProps> = (props: IProps) => {
  // Flutter fonts readily available across platforms
  const flutterFonts = [
    { value: 'Roboto', label: 'Roboto (Default Android)' },
    { value: 'Ubuntu', label: 'Ubuntu' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Raleway', label: 'Raleway' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Work Sans', label: 'Work Sans' },
    { value: 'Merriweather', label: 'Merriweather' },
    { value: 'Playfair Display', label: 'Playfair Display' },
    { value: 'Dancing Script', label: 'Dancing Script' },
    { value: 'Pacifico', label: 'Pacifico' },
  ];

  // Common timezones for mobile apps
  const timezones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
    { value: 'Europe/London', label: 'GMT (London)' },
    { value: 'Europe/Paris', label: 'CET (Paris)' },
    { value: 'Europe/Berlin', label: 'CET (Berlin)' },
    { value: 'Europe/Rome', label: 'CET (Rome)' },
    { value: 'Europe/Madrid', label: 'CET (Madrid)' },
    { value: 'Europe/Amsterdam', label: 'CET (Amsterdam)' },
    { value: 'Asia/Tokyo', label: 'JST (Tokyo)' },
    { value: 'Asia/Shanghai', label: 'CST (Shanghai)' },
    { value: 'Asia/Seoul', label: 'KST (Seoul)' },
    { value: 'Asia/Singapore', label: 'SGT (Singapore)' },
    { value: 'Asia/Mumbai', label: 'IST (Mumbai)' },
    { value: 'Asia/Dubai', label: 'GST (Dubai)' },
    { value: 'Australia/Sydney', label: 'AEST (Sydney)' },
    { value: 'Australia/Melbourne', label: 'AEST (Melbourne)' },
    { value: 'Australia/Perth', label: 'AWST (Perth)' },
    { value: 'Pacific/Auckland', label: 'NZST (Auckland)' },
    { value: 'America/Sao_Paulo', label: 'BRT (São Paulo)' },
    { value: 'America/Mexico_City', label: 'CST (Mexico City)' },
    { value: 'Africa/Cairo', label: 'EET (Cairo)' },
    { value: 'Africa/Johannesburg', label: 'SAST (Johannesburg)' },
  ];

  const [formData, setFormData] = useState<IFmsApp>({
    IsActive: true,
    Name: '',
    Description: '',
    LogoUrl: '',
    Theme: {
      ConfigVersion: '1.0',
      PrimaryColor: '#007bff',
      PrimaryTextColor: '#ffffff',
      SecondaryColor: '#6c757d',
      SecondaryTextColor: '#ffffff',
      BackgroundColor: '#ffffff',
      BackgroundImageUrl: '',
      FontFamily: 'Roboto',
      TextColor: '#333333',
      SplashImageUrl: '',
    },
    Config: {
      Version: '1.0',
      EnableNotifications: true,
      DefaultLanguage: 'en',
      TimeZone: 'America/Denver',
      UseProfile: false,
      UseLogin: false,
      UseLocationServices: false,
    },
    AppShortkey: '',
    HomeScreenPublicId: '',
    Screens: [],
  });
  const [fileDic, setFileDic] = useState<Record<string, File>>({});

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (props.app) {
      setFormData({
        ...props.app,
        Theme: props.app.Theme || formData.Theme,
        Config: props.app.Config || formData.Config,
        Screens: props.app.Screens || [],
      });
    }
  }, [props.app]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.Name || formData.Name.trim() === '') {
      newErrors.Name = 'App name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors before saving.');
      return;
    }

    props.onSave(formData, fileDic);
  };

  const handleRemove = () => {
    if (props.app && props.app.Id) {
      props.onRemove(props.app);
    }
  };

  const updateFormData = (field: keyof IFmsApp, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const updateTheme = (field: keyof IAppTheme, value: string) => {
    setFormData((prev) => ({
      ...prev,
      Theme: {
        ...prev.Theme!,
        [field]: value,
      },
    }));
  };

  const updateConfig = (field: keyof IAppConfig, value: any) => {
    setFormData((prev) => ({
      ...prev,
      Config: {
        ...prev.Config!,
        [field]: value,
      },
    }));
  };

  return (
    <>
      <div className="mobile-app-editor">
        <Tabs>
          <TabList>
            <Tab>Basic Information</Tab>
            <Tab>Theme Configuration</Tab>
            <Tab>App Configuration</Tab>
            <Tab>App Screens</Tab>
          </TabList>
          <TabPanel>
            {/* Basic Information Section */}
            <div className="form-section">
              <h4>Basic Information</h4>
              <div className="form-horizontal">
                <div className="form-group">
                  <label className="col-sm-3 control-label">App Name *</label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className={`form-control ${errors.Name ? 'has-error' : ''}`}
                      value={formData.Name || ''}
                      onChange={(e) => updateFormData('Name', e.target.value)}
                      placeholder="Enter app name"
                    />
                    {errors.Name && <span className="text-danger">{errors.Name}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Description</label>
                  <div className="col-sm-9">
                    <textarea
                      className="form-control"
                      rows={3}
                      value={formData.Description || ''}
                      onChange={(e) => updateFormData('Description', e.target.value)}
                      placeholder="Enter app description"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Logo</label>
                  <div className="col-sm-9">
                    <EasyFileInput
                      htmlId="logo-upload"
                      exts=".jpg,.jpeg,.png,.gif,.svg,.webp,.heic"
                      fileSize={10}
                      value={formData.LogoUrl || ''}
                      onSelectFile={(file: File) => {
                        // Handle file upload if needed
                        console.log('Logo file selected:', file);
                        let newFileDic = { ...fileDic };
                        newFileDic['logo'] = file;
                        setFileDic(newFileDic);
                      }}
                      onSelect={(value: string) => {
                        updateFormData('LogoUrl', value);
                      }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Active</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.IsActive || false} onChange={(e) => updateFormData('IsActive', e.target.checked)} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">App Short Key</label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.AppShortkey || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                      placeholder="Auto-generated identifier"
                    />
                    <small className="help-block">This key helps end users identify the correct app</small>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Home Screen</label>
                  <div className="col-sm-9">
                    <select
                      className="form-control"
                      value={formData.HomeScreenPublicId || ''}
                      onChange={(e) => updateFormData('HomeScreenPublicId', e.target.value)}
                    >
                      <option value="">-- None --</option>
                      {formData.Screens?.map((screen) => (
                        <option key={screen.PublicId} value={screen.PublicId}>
                          {screen.MenuTitle || screen.Title}
                        </option>
                      ))}
                    </select>
                    <small className="help-block">Select which screen should be the app's home screen</small>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            {/* Theme Configuration Section */}
            <div className="form-section">
              <h4>Theme Configuration</h4>
              <div className="form-horizontal">
                <div className="form-group">
                  <label className="col-sm-3 control-label">Primary Color</label>
                  <div className="col-sm-9">
                    <ColorPicker value={formData.Theme?.PrimaryColor} onChange={(value) => updateTheme('PrimaryColor', value || '#007bff')} placeholder="Primary color" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Primary Text Color</label>
                  <div className="col-sm-9">
                    <ColorPicker value={formData.Theme?.PrimaryTextColor} onChange={(value) => updateTheme('PrimaryTextColor', value || '#ffffff')} placeholder="Primary text color" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Secondary Color</label>
                  <div className="col-sm-9">
                    <ColorPicker value={formData.Theme?.SecondaryColor} onChange={(value) => updateTheme('SecondaryColor', value || '#6c757d')} placeholder="Secondary color" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Secondary Text Color</label>
                  <div className="col-sm-9">
                    <ColorPicker value={formData.Theme?.SecondaryTextColor} onChange={(value) => updateTheme('SecondaryTextColor', value || '#ffffff')} placeholder="Secondary text color" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Background Color</label>
                  <div className="col-sm-9">
                    <ColorPicker value={formData.Theme?.BackgroundColor} onChange={(value) => updateTheme('BackgroundColor', value || '#ffffff')} placeholder="Background color" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Text Color</label>
                  <div className="col-sm-9">
                    <ColorPicker value={formData.Theme?.TextColor} onChange={(value) => updateTheme('TextColor', value || '#333333')} placeholder="Text color" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Font Family</label>
                  <div className="col-sm-9">
                    <select className="form-control" value={formData.Theme?.FontFamily || 'Roboto'} onChange={(e) => updateTheme('FontFamily', e.target.value)}>
                      {flutterFonts.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Background Image</label>
                  <div className="col-sm-9">
                    <EasyFileInput
                      htmlId="background-upload"
                      exts=".jpg,.jpeg,.png,.gif,.svg,.webp,.heic"
                      fileSize={20}
                      value={formData.Theme?.BackgroundImageUrl || ''}
                      onSelectFile={(file: File) => {
                        // Handle file upload if needed
                        console.log('Background image file selected:', file);
                        let newFileDic = { ...fileDic };
                        newFileDic['background'] = file;
                        setFileDic(newFileDic);
                      }}
                      onSelect={(value: string) => {
                        updateTheme('BackgroundImageUrl', value);
                      }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Splash Image</label>
                  <div className="col-sm-9">
                    <EasyFileInput
                      htmlId="splash-upload"
                      exts=".jpg,.jpeg,.png,.gif,.svg,.webp,.heic"
                      fileSize={15}
                      value={formData.Theme?.SplashImageUrl || ''}
                      onSelectFile={(file: File) => {
                        // Handle file upload if needed
                        console.log('Splash image file selected:', file);
                        let newFileDic = { ...fileDic };
                        newFileDic['splash'] = file;
                        setFileDic(newFileDic);
                      }}
                      onSelect={(value: string) => {
                        updateTheme('SplashImageUrl', value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            {/* App Configuration Section */}
            <div className="form-section">
              <h4>App Configuration</h4>
              <div className="form-horizontal">
                <div className="form-group">
                  <label className="col-sm-3 control-label">Version</label>
                  <div className="col-sm-9">
                    <input type="text" className="form-control" value={formData.Config?.Version || ''} onChange={(e) => updateConfig('Version', e.target.value)} placeholder="1.0" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Enable Notifications</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.Config?.EnableNotifications || false} onChange={(e) => updateConfig('EnableNotifications', e.target.checked)} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Default Language</label>
                  <div className="col-sm-9">
                    <select className="form-control" value={formData.Config?.DefaultLanguage || 'en'} onChange={(e) => updateConfig('DefaultLanguage', e.target.value)}>
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Time Zone</label>
                  <div className="col-sm-9">
                    <select className="form-control" value={formData.Config?.TimeZone || 'UTC'} onChange={(e) => updateConfig('TimeZone', e.target.value)}>
                      {timezones.map((timezone) => (
                        <option key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label">Use Profile</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.Config?.UseProfile || false} onChange={(e) => updateConfig('UseProfile', e.target.checked)} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Use Login</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.Config?.UseLogin || false} onChange={(e) => updateConfig('UseLogin', e.target.checked)} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Use Location Services</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.Config?.UseLocationServices || false} onChange={(e) => updateConfig('UseLocationServices', e.target.checked)} />
                  </div>
                </div>

              </div>
            </div>
          </TabPanel>
          <TabPanel>
            {/* App Screens Section */}
            <div className="form-section">
              <h4>App Screens</h4>
              <div className="form-horizontal">
                <div className="form-group">
                  <label className="col-sm-3 control-label">Configured Screens</label>
                  <div className="col-sm-9">
                    <div className="flex" style={{ alignItems: 'center', gap: '15px' }}>
                      <div>
                        <strong>{formData.Screens?.length || 0}</strong> screen{(formData.Screens?.length || 0) !== 1 ? 's' : ''} configured
                      </div>
                    </div>
                    {formData.Screens && formData.Screens.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <small className="text-muted">
                          Screens:{' '}
                          {formData.Screens.sort((a, b) => a.OrderInMenu - b.OrderInMenu)
                            .map((s) => s.MenuTitle)
                            .join(', ')}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </Tabs>

        {/* Action Buttons */}
        <div className="form-section">
          <div className="flex-between">
            <div>{props.app && props.app.Id && <Button type="remove" text="Remove App" onClick={handleRemove} />}</div>
            <div>
              <Button type="close" text="Cancel" onClick={props.onClose} />
              <Button type="save" text="Save App" onClick={handleSave} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileAppEditor;
